export const APPROVED_CATEGORY_COUNT = 10;
export const APPROVED_INTEREST_COUNT = 802;
export const VISIBLE_ROWS = 80;
export const MAX_CATALOG_DRAFT_BYTES = 1 << 20;

export const interestRows = catalog => (catalog?.entries || []).filter(entry => entry.kind === 'interest');

export const catalogBytes = catalog => catalog ? new Blob([JSON.stringify(catalog)]).size : 0;

export function validateCatalog(catalog) {
  if (!catalog || typeof catalog !== 'object' || !Array.isArray(catalog.categories) || !Array.isArray(catalog.entries)) return '카탈로그 JSON의 categories와 entries 배열을 확인해 주세요.';
  if (!catalog.catalog_version?.trim()) return '새 catalog_version을 입력해 주세요.';
  const ids = new Set();
  for (const entry of catalog.entries) {
    if (!entry.id || !entry.kind || !entry.labels || typeof entry.labels !== 'object') return '항목의 ID·종류·번역을 확인해 주세요.';
    const key = `${entry.kind}:${entry.id}`;
    if (ids.has(key)) return `중복 ID: ${key}`;
    ids.add(key);
  }
  return '';
}

function diffGroup(before, after, keyOf, labelOf) {
  const previous = new Map(before.map(item => [keyOf(item), item]));
  const next = new Map(after.map(item => [keyOf(item), item]));
  const changes = [];
  for (const [key, item] of next) {
    const old = previous.get(key);
    if (!old) changes.push({ key, kind: 'added', label: labelOf(item), after: item });
    else if (JSON.stringify(old) !== JSON.stringify(item)) changes.push({ key, kind: 'changed', label: labelOf(item), fields: changedFields(old, item), before: old, after: item });
  }
  for (const [key, item] of previous) if (!next.has(key)) changes.push({ key, kind: 'removed', label: labelOf(item), before: item });
  return changes;
}

function changedFields(before, after) {
  const result = [];
  for (const key of new Set([...Object.keys(before), ...Object.keys(after)])) {
    if (JSON.stringify(before[key]) === JSON.stringify(after[key])) continue;
    if (key === 'labels' || key === 'search_aliases') {
      for (const locale of new Set([...Object.keys(before[key] || {}), ...Object.keys(after[key] || {})])) {
        if (JSON.stringify(before[key]?.[locale]) !== JSON.stringify(after[key]?.[locale])) result.push(`${key}.${locale}`);
      }
    } else result.push(key);
  }
  return result;
}

export function catalogChanges(published, candidate) {
  if (!published || !candidate) return [];
  const metadata = changedFields(published, candidate).filter(field => !['categories', 'entries'].includes(field)).map(field => ({ key: `catalog:${field}`, kind: 'changed', label: '카탈로그 설정', fields: [field], before: published[field], after: candidate[field] }));
  return [
    ...metadata,
    ...diffGroup(published.categories || [], candidate.categories || [], item => `category:${item.id}`, item => item.labels?.ko || item.id),
    ...diffGroup(published.entries || [], candidate.entries || [], item => `${item.kind}:${item.id}`, item => item.labels?.ko || item.id),
  ];
}

// One content-reviewed internal TEST proposal; this is not native/official-name approval.
export const INTERNAL_TEST_PROPOSAL = Object.freeze({
  version: 'profile-tags-test-20261015-802-v1',
  fileSHA: '0ba5c07201cc118b0dae07270c89780c626725959f099cd296da9bbc7e58ddf5',
  goSHA: '8177bd99bf2c81aa0cef631ad4a54f9fa249ac18091d7eee828518e291089cc1',
  canonicalSHA: '66e9d5d191a7fcfcd5e483a156e4d61e6a4a4bdf723d99d03f43819f604ed8ed',
});

// Exact content approved for the 1.9.10 Production internal candidate; full language review is separate.
export const INTERNAL_PROD_PROPOSAL = Object.freeze({
  version: 'profile-tags-20261017-802-v1',
  fileSHA: 'ca2ec5da2af0ee77456f56e2c30c64118f763025cc8327099dcd4ba1d8ab3fb7',
  goSHA: 'd2cc46b0005f07693a322af33237febdb5c0fec1b1b351126f456f142ed602b4',
  canonicalSHA: '12f1cff011f76a3521b7063460323bf3572f3297746ec26d697e95d4c7c29886',
});
export const proposalForEnvironment = environment => environment === 'prod' ? INTERNAL_PROD_PROPOSAL : INTERNAL_TEST_PROPOSAL;

// Narrow TEST-only cache probe contract; the provider also checks its runtime flag and user references.
export const TEST_CACHE_PROBE = Object.freeze({
  id: 'qa_cache_probe_20261016',
  addVersion: 'profile-tags-test-20261016-qa-add-v1',
  restoreVersion: 'profile-tags-test-20261016-qa-restore-v1',
});

export function cacheProbeEntry(catalog) {
  return { kind: 'interest', id: TEST_CACHE_PROBE.id, category_id: 'daily_rest', sort_order: 9999,
    selectable: true, labels: Object.fromEntries(Object.keys(catalog.categories[0]?.labels || {}).map(locale => [locale, 'QA cache probe 20261016'])) };
}

export function cacheProbeBaseline(catalog) {
  return { ...catalog, catalog_version: INTERNAL_TEST_PROPOSAL.version,
    entries: catalog.entries.filter(entry => !(entry.kind === 'interest' && entry.id === TEST_CACHE_PROBE.id)) };
}

export function cacheProbeCandidate(published, remove = false) {
  const baseline = cacheProbeBaseline(published);
  return { ...baseline, catalog_version: remove ? TEST_CACHE_PROBE.restoreVersion : TEST_CACHE_PROBE.addVersion,
    entries: remove ? baseline.entries : [...baseline.entries, cacheProbeEntry(baseline)] };
}

function exactProbeAdded(catalog) {
  return catalog?.catalog_version === TEST_CACHE_PROBE.addVersion
    && canonicalCatalogJSON(catalog.entries[catalog.entries.length - 1]) === canonicalCatalogJSON(cacheProbeEntry(catalog));
}

// JSON object key order can change during MySQL/provider read-back; arrays stay ordered.
export function canonicalCatalogJSON(value) {
  const sort = item => Array.isArray(item) ? item.map(sort)
    : item && typeof item === 'object'
      ? Object.fromEntries(Object.keys(item).sort().map(key => [key, sort(item[key])])) : item;
  return JSON.stringify(sort(value));
}

export async function sha256Text(text) {
  const bytes = new TextEncoder().encode(text);
  const digest = await window.crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
}

// The provider remains authoritative for locale/search-term validation and version history.
export function publicationReady(catalog, { environment, publishedRevision, canonicalSHA, published, baselineSHA, publishedBaselineSHA } = {}) {
  if (environment === 'prod') return publishedRevision === 0
    && !validateCatalog(catalog) && canonicalSHA === INTERNAL_PROD_PROPOSAL.canonicalSHA
    && catalog.catalog_version === INTERNAL_PROD_PROPOSAL.version
    && catalog.categories.length === APPROVED_CATEGORY_COUNT
    && interestRows(catalog).length === APPROVED_INTEREST_COUNT
    && catalog.translation_review_pending === false;
  if (environment !== 'test' || validateCatalog(catalog) || !/^[a-f0-9]{64}$/.test(canonicalSHA || '')) return false;
  if (Number.isSafeInteger(publishedRevision) && publishedRevision > 0) {
    if (!published || !/^[A-Za-z0-9._-]{1,64}$/.test(catalog.catalog_version)
      || catalog.catalog_version === published.catalog_version || catalog.translation_review_pending !== false) return false;
    const hasProbe = value => value.entries.some(entry => entry.kind === 'interest' && entry.id === TEST_CACHE_PROBE.id);
    const probeVersions = [TEST_CACHE_PROBE.addVersion, TEST_CACHE_PROBE.restoreVersion];
    if (hasProbe(catalog) || hasProbe(published) || probeVersions.includes(catalog.catalog_version)) {
      if (baselineSHA !== INTERNAL_TEST_PROPOSAL.canonicalSHA || publishedBaselineSHA !== INTERNAL_TEST_PROPOSAL.canonicalSHA) return false;
      if (publishedRevision === 1 && published.catalog_version === INTERNAL_TEST_PROPOSAL.version && !hasProbe(published)) return exactProbeAdded(catalog);
      return exactProbeAdded(published) && !hasProbe(catalog) && catalog.catalog_version === TEST_CACHE_PROBE.restoreVersion;
    }
    const entries = new Map(catalog.entries.map(entry => [`${entry.kind}:${entry.id}`, entry]));
    return published.entries.every(prior => {
      const current = entries.get(`${prior.kind}:${prior.id}`);
      if (!current) return false; // Provider forbids deleting any existing stable ID.
      if (prior.kind !== 'interest') return canonicalCatalogJSON(prior) === canonicalCatalogJSON(current);
      return prior.selectable !== false || current.selectable === false;
    });
  }
  return environment === 'test' && publishedRevision === 0
    && canonicalSHA === INTERNAL_TEST_PROPOSAL.canonicalSHA
    && catalog?.catalog_version === INTERNAL_TEST_PROPOSAL.version
    && catalog?.categories?.length === APPROVED_CATEGORY_COUNT
    && interestRows(catalog).length === APPROVED_INTEREST_COUNT
    && catalog.translation_review_pending === false;
}
